import { Component, input, effect, untracked, signal, output, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, pairwise } from 'rxjs';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { AutofocusDirective } from 'src/app/directives/autofocus.directive';

import { ClarityModule } from '@clr/angular';
import { FormGroupOfType } from 'src/app/types/shared';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';

export interface HerstellDaten {
  ID: number;
  Hersteller: string;
  Herstelldatum: string; // DATE
  Einbaufirma: string;
  Einbaudatum: string; // DATE
  Erneuerungsjahr: number; // YYYY
  Nutzungsdauer: number;
  DauerGewaehrleistung: number;
  EinheitGewaehrleistung: 'Tag' | 'Monat' | 'Jahr'; // Tag, Monat, Jahr
  EndeGewaehrleistung: string; // DATE
}

@Component({
  selector: 'app-herstell-daten-modal',
  templateUrl: './herstell-daten-modal.component.html',
  styleUrls: ['./herstell-daten-modal.component.scss'],
  imports: [
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    AutofocusDirective,
    MrTranslatePipe,
    BackendLocaleDatePipe,
  ],
})
export class HerstellDatenModalComponent {
  private readonly apiService = inject(APIService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  protected readonly localeDate = inject(BackendLocaleDatePipe);

  /** @emits true when saved, for reload */
  readonly onClose = output<boolean>();

  protected readonly disable = input(false);
  protected readonly ospezid = input.required<number>();

  private readonly dataResource = rxResource({
    params: this.ospezid,
    stream: ({ params }) => this.apiService.getHerstelldaten(params),
  });

  protected readonly isModalOpen = signal<boolean>(undefined);

  protected herstellDatenForm = new FormGroupOfType<HerstellDaten>({
    ID: new FormControl<number>(null),
    Hersteller: new FormControl('', [Validators.required]),
    Herstelldatum: new FormControl(BackendLocaleDatePipe.now, [Validators.required]),
    Einbaufirma: new FormControl('', [Validators.required]),
    Einbaudatum: new FormControl(BackendLocaleDatePipe.now, [Validators.required]),
    Erneuerungsjahr: new FormControl<number>(null, [Validators.required]),
    Nutzungsdauer: new FormControl<number>(null, [Validators.required]),
    DauerGewaehrleistung: new FormControl(2, [Validators.required]),
    EinheitGewaehrleistung: new FormControl<'Tag' | 'Monat' | 'Jahr'>('Jahr', [Validators.required]),
    EndeGewaehrleistung: new FormControl(dayjs().add(2, 'years').format(BackendLocaleDatePipe.format), [
      Validators.required,
    ]),
  });

  constructor() {
    effect(() => {
      const herstelldaten = this.dataResource.value();
      if (herstelldaten.Erneuerungsjahr == 0) {
        herstelldaten.Erneuerungsjahr = dayjs(herstelldaten.Herstelldatum)
          .add(herstelldaten.Nutzungsdauer, 'years')
          .year();
      }

      untracked(() => {
        this.herstellDatenForm.patchValue(herstelldaten);

        if (herstelldaten.EndeGewaehrleistung == null) {
          this.calcEndeGewährleistung(
            herstelldaten.Einbaudatum,
            herstelldaten.DauerGewaehrleistung,
            herstelldaten.EinheitGewaehrleistung
          );
        }

        this.isModalOpen.set(true);
      });
    });

    effect(() => {
      if (this.isModalOpen() === false) this.onClose.emit(false);
    });

    effect(() => {
      if (this.disable()) this.herstellDatenForm.disable();
    });

    this.herstellDatenForm.valueChanges
      .pipe(pairwise(), takeUntilDestroyed())
      .subscribe(
        ([old, { Einbaudatum: start, DauerGewaehrleistung: dauer, EinheitGewaehrleistung: einheit }]: [
          Partial<HerstellDaten>,
          Partial<HerstellDaten>,
        ]) => {
          if (old.Einbaudatum != start || old.DauerGewaehrleistung != dauer || old.EinheitGewaehrleistung != einheit) {
            this.calcEndeGewährleistung(start, dauer, einheit);
            this.herstellDatenForm.markAsDirty();
          }
        }
      );
  }

  protected async saveHerst() {
    if (this.herstellDatenForm.pristine) this.onClose.emit(false);
    else {
      const sendObj: HerstellDaten = this.herstellDatenForm.getRawValue();
      const { success, error } = await firstValueFrom(this.apiService.setHerstelldaten(this.ospezid(), sendObj));

      if (success) {
        this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
        this.onClose.emit(true);
      } else this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  private calcEndeGewährleistung(start: string, dauer: number, einheit: string) {
    einheit =
      einheit == 'Tag' ? 'days'
      : einheit == 'Monat' ? 'months'
      : 'years';
    this.herstellDatenForm.get('EndeGewaehrleistung').setValue(
      dayjs(start || undefined)
        .add(dauer ?? 2, <dayjs.ManipulateType>einheit)
        .format(BackendLocaleDatePipe.format)
    );
  }
}
